import { useEffect, useState, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { ToastHost } from '@/components/ui/ToastHost';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { cn } from '@/lib/cn';
import { PAGE_ACTIONS_SLOT_ID } from './PageActions';

export interface PageHeading {
  title: string;
  subtitle?: string;
  crumb?: { label: string; to: string };
}

export interface AppShellProps {
  sidebar: ReactNode;
  topBar: ReactNode;
  collapsed: boolean;
  heading: PageHeading;
  children: ReactNode;
}

export function AppShell({ sidebar, topBar, collapsed, heading, children }: AppShellProps) {
  return (
    <div className="min-h-screen bg-neutral-50">
      {sidebar}

      <div
        className={cn(
          'flex min-h-screen flex-col transition-[padding]',
          collapsed ? 'lg:pl-16' : 'lg:pl-56',
        )}
      >
        {topBar}

        <main className="mx-auto flex w-full max-w-[1440px] flex-col gap-4 p-4 sm:gap-6 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              {heading.crumb && (
                <Link
                  to={heading.crumb.to}
                  className="inline-flex items-center gap-1 text-xs font-bold text-brand-600 no-underline hover:underline"
                >
                  <ArrowLeft size={13} aria-hidden />
                  {heading.crumb.label}
                </Link>
              )}
              <h1 className="mt-0.5 truncate text-2xl font-bold text-neutral-900">
                {heading.title}
              </h1>
              {heading.subtitle && (
                <p className="mt-1 text-[13px] text-neutral-500">{heading.subtitle}</p>
              )}
            </div>

            <div
              id={PAGE_ACTIONS_SLOT_ID}
              className="flex shrink-0 flex-wrap items-center gap-2.5 empty:hidden"
            />
          </div>

          {children}
        </main>
      </div>

      <ToastHost />
    </div>
  );
}

export function useShellChrome(storageKey = 'edify_sidebar_collapsed') {
  const [collapsed, setCollapsed] = useLocalStorage(storageKey, false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();
  const isWide = useBreakpoint('lg');

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  return {
    collapsed: isWide && collapsed,
    toggleCollapsed: () => setCollapsed((value) => !value),
    drawerOpen: !isWide && drawerOpen,
    openDrawer: () => setDrawerOpen(true),
    closeDrawer: () => setDrawerOpen(false),
  };
}
